// src/components/universalSearchBar/UniversalSearchPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../components/context/authContext";
import { useNotifications } from "../../components/context/notificationContext";
import { isSuspendedFrom } from "../../utils/membersSiteAccessPermissions";
import {
  BigRemoteButton,
  FieldButton,
  ImgTopBtn,
  MemberSlider,
  SolidColorButton,
} from "../../components/buttons";
import LightBeam from "../../components/lightBeam/lightBeam";
import {
  SkitsOlympicLogoIcon,
  PeopleIcon,
  LeftArrow,
} from "../../components/icons";
import { apiSearchMembersByUsername } from "../../api/backend/user";
import {
  apiSearchSkitsByTitle,
  apiReportSkit,
  apiDeleteSkitIdFromSourceIdAction,
} from "../../api/backend/skits";
import { apiRecordUserSkitNotInterested } from "../../api/backend/user";
import ReportModal from "../../components/reportModal/reportModal";
import SkitAdCard from "../../components/skitAd/skitAdCard";
import Loader from "../../components/loader/loader";
import "./universalSearchPage.scss";

const UniversalSearchPage = ({ initialQuery = "" }) => {
  const navigate = useNavigate();
  const {
    currentMemberId,
    handleLogout,
    setNotInterested,
    setSuccessFlashMessage,
  } = useAuth();
  const { notificationsCountFetchedFromMiddleware } = useNotifications();
  const [absentValueError, setAbsentValueError] = useState("");
  const [universalSearchFieldFocused, setUniversalSearchFieldFocused] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState(initialQuery);
  const [mode, setMode] = useState(null); // "user" | "skit"
  const [userResults, setUserResults] = useState([]);
  const [skitResults, setSkitResults] = useState([]);
  const [reportingSkit, setReportingSkit] = useState(null);
  const [error, setError] = useState("");
  const [lightBeamOn, setLightBeamOn] = useState(false);

  // run search whenever query or mode changes (debounced)
  useEffect(() => {
    if (!mode) return;
    const timer = setTimeout(() => handleSearch(mode), 300);
    return () => clearTimeout(timer);
  }, [query, mode]);

  const handleSearch = async (m) => {
    if (!query.trim()) {
      setAbsentValueError("Please enter a user name or skit title");
      return;
    }
    setAbsentValueError("");
    setError("");
    setLoading(true);
    setMode(m);
    try {
      if (m === "user") {
        const users = await apiSearchMembersByUsername({
          membersUserName: query,
        });
        setUserResults(users);
      } else {
        const { data } = await apiSearchSkitsByTitle({ title: query });
        setSkitResults(data?.data || []);
      }
    } catch (err) {
      console.error(err);
      if (m === "user") setUserResults([]);
      else setSkitResults([]);
    } finally {
      setLoading(false);
    }
  };

  const triggerLightBeam = () => {
    setLightBeamOn(true);
    setTimeout(() => setLightBeamOn(false), 500);
  };

  const goToProfile = (id) => {
    if (id) navigate(`/user-profile/${id}`);
  };

  // --- DELETE SKIT ---
  const handleDeleteSourceIdRelatedIdSkit = useCallback((skitId) => {
    apiDeleteSkitIdFromSourceIdAction({ skitId })
      .then((response) => {
        if (response.status === 200) {
          setSkitResults((prev) => prev.filter((s) => s.id !== skitId));
        } else {
          alert("Failed to delete the skit");
        }
      })
      .catch((err) => {
        console.error("Error deleting skit:", err);
        alert("An error occurred while deleting the skit");
      });
  }, []);

  // --- MARK NOT INTERESTED ---
  const handleNotInterested = useCallback(
    async (skit) => {
      try {
        const resp = await apiRecordUserSkitNotInterested({
          loggedInAccountHolderId: currentMemberId,
          activity: "notInterested",
          relatedId: skit.id,
          targetId: skit.sourceId,
        });
        // suspension eject (your pattern)
        if (isSuspendedFrom(resp) || isSuspendedFrom(resp?.data)) {
          await handleLogout();
          return;
        }
        // 🔔 hydrate unread badge if backend included a count
        const count = resp?.data?.unreadNotificationCount;
        if (typeof count === "number") {
          notificationsCountFetchedFromMiddleware(count);
        }
        if (resp?.data?.success) {
          // hide the skit immediately
          setSkitResults((prev) => prev.filter((s) => s.id !== skit.id));
          // optional: also tag it locally so other views respect it without refetch
          setNotInterested?.((prev) =>
            Array.isArray(prev) ? [...prev, skit.id] : [skit.id]
          );
        } else {
          console.error("Failed to mark not interested:", resp?.data);
          setError("Failed to mark skit as 'not interested'");
        }
      } catch (err) {
        // suspension on error path too
        if (isSuspendedFrom(err) || isSuspendedFrom(err?.response)) {
          await handleLogout();
          return;
        }
        // 🔔 hydrate badge from error payload if present
        const errCount =
          err?.response?.data?.unreadNotificationCount ??
          err?.unreadNotificationCount ??
          null;
        if (typeof errCount === "number") {
          notificationsCountFetchedFromMiddleware(errCount);
        }
        console.error("Error marking not interested:", err);
        setError("Failed to mark skit as 'not interested'");
      }
    },
    [currentMemberId, notificationsCountFetchedFromMiddleware, handleLogout]
  );

  // --- REPORT SKIT ---
  // uses: isSuspendedFrom, notificationsCountFetchedFromMiddleware, handleLogout? (optional)
  const handleReport = async (choices) => {
    // tiny inline flash helper (no global UI plumbing)
    const flash = (msg, ms = 1800) => {
      setSuccessFlashMessage(msg);
      // keep one timer per function
      clearTimeout(handleReport._t);
      handleReport._t = setTimeout(() => setSuccessFlashMessage(null), ms);
    };
    try {
      const res = await apiReportSkit({
        loggedInAccountHolderId: currentMemberId, // keep nomenclature
        skitId: reportingSkit.id,
        reason: JSON.stringify(choices),
      });
      // eject on suspension
      if (isSuspendedFrom(res) || isSuspendedFrom(res?.data)) {
        await handleLogout?.();
        return;
      }
      // 🔔 hydrate from body then header
      const bodyCount = Number(res?.data?.unreadNotificationCount ?? res?.unreadNotificationCount);
      if (Number.isFinite(bodyCount)) notificationsCountFetchedFromMiddleware(bodyCount);
      const headerCount = Number(res?.headers?.["x-unread-notifications"]);
      if (Number.isFinite(headerCount)) notificationsCountFetchedFromMiddleware(headerCount);
      // flash success (no alert)
      flash("Report sent ✅");
      // if suspended, pull it from results
      const skitStatus = res?.data?.data?.skitStatus ?? res?.data?.skitStatus;
      if (skitStatus === "suspended") {
        setSkitResults((prev) => prev.filter((s) => s.id !== reportingSkit.id));
      }
    } catch (error) {
      // suspension on error path too
      if (isSuspendedFrom(error) || isSuspendedFrom(error?.response)) {
        await handleLogout?.();
        return;
      }
      // hydrate from error payload if present
      const errCount =
        error?.response?.data?.unreadNotificationCount ??
        error?.unreadNotificationCount ??
        null;
      if (typeof errCount === "number") {
        notificationsCountFetchedFromMiddleware(errCount);
      }
      flash("Could not submit report. Try again.", 2200);
    } finally {
      setReportingSkit(null);
    }
  };
  

  return (
    <div id="universal-search-bar-page" className="universal-search-bar">
      <SolidColorButton
        solidColorBtnImage={
          <LeftArrow
            height="15px"
            width="15px"
            fillColor="#964b00"
            strokeWidth="4"
            strokeColor="#964b00"
          />
        }
        className="gold-btn"
        solidColorBtnText="Back"
        solidColorBtnFontWeight="800"
        solidColorBtnFontFamily="helvetica"
        solidColorBtnTextFontSize=".75rem"
        solidColorBtnFontColor="#964b00"
        solidColorBtnWidth="100px"
        solidColorBtnBoxShadow="0 4px 3px 1px #000000bb, 0 6px 8px #000000bb, 0 -4px 4px #000000bb, 0 -6px 4px #000000bb, inset 0 0 3px 0 #000000bb"
        onClick={() => navigate(-1)}
      />
      <div className="universal-search-input-wrapper blinker-wrapper">
        <SkitsOlympicLogoIcon
          className="universal-search-bar-icon"
          fillColor="var(--color-gold)"
        />
        <input
          type="text"
          className={`universal-search-input${absentValueError ? " required-message" : ""}`}
          placeholder={absentValueError || "Username or Skit Title?…"}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setAbsentValueError("");
          }}
          onFocus={() => setUniversalSearchFieldFocused(true)}
          onBlur={() => setUniversalSearchFieldFocused(false)}
        />
        {!(query || universalSearchFieldFocused) && (
          <span className="blinking-cursor-gold-color universal-search-cursor">
            |
          </span>
        )}
        {query && <FieldButton onClick={() => setQuery("")}>Clear</FieldButton>}
        {!query && (
          <>
            <BigRemoteButton
              mainBtnText="Search"
              onMainClick={() => {
                triggerLightBeam();
                handleSearch(mode || "skit");
              }}
            />
            <LightBeam
              lightBeamStyles={{
                transform: "translateX(2%) translateY(-100%)",
                backgroundColor: lightBeamOn
                  ? "var(--color-corveteRed)"
                  : "var(--color-redish)",
              }}
            />
          </>
        )}
      </div>
      {/* — MODE SELECTORS — */}
      <div className="universal-mode-buttons">
        <ImgTopBtn
          onClick={() => handleSearch("user")}
          image={
            <PeopleIcon
              height="20"
              width="20"
              fillColor="var(--color-bloodred)"
              style={{ marginRight: "5px" }}
            />
          }
          imgTopBtnText="Search by Username"
          imgTopBtnFontColor="var(--color-bloodred)"
          imgTopBtnWidth="200px"
        />
        <ImgTopBtn
          onClick={() => handleSearch("skit")}
          image={
            <SkitsOlympicLogoIcon
              height="20"
              width="20"
              fillColor="var(--color-bloodred)"
              style={{ marginRight: "5px" }}
            />
          }
          imgTopBtnText="Search by Skit Title"
          imgTopBtnFontColor="var(--color-bloodred)"
          imgTopBtnWidth="200px"
        />
      </div>

      {error && <div className="universal-search-error">{error}</div>}
      {loading && <Loader className="universal-search-loader" />}

      {/* — RESULTS — */}
      <div className="universal-search-results">
        {mode === "user" &&
          (userResults.length > 0 ? (
            userResults.map((u) => (
              <div key={u.id} className="universal-user-entry">
                <MemberSlider
                  user={u}
                  slideBtnText="View Profile"
                  onHandleSlideEnd={() => goToProfile(u.id)}
                />
                <div className="universal-user-details">
                  <span className="username">{u.username}</span>
                  <span className="fullname">
                    {u.firstName} {u.lastName}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <p>No users found</p>
          ))}

        {mode === "skit" &&
          (skitResults.length > 0 ? (
            <div className="universal-skit-thumbs-grid">
              {skitResults.map((s) => (
                <SkitAdCard
                  key={s.id}
                  specificSkitData={s}
                  currentMemberId={currentMemberId}
                  onDelete={() => handleDeleteSourceIdRelatedIdSkit(s.id)}
                  onNotInterested={() => handleNotInterested(s)}
                  updateSkitsData={() => {}}
                  isUploadVideoFormOpen={false}
                  onBlowWhistle={() => setReportingSkit(s)}
                />
              ))}
            </div>
          ) : (
            <p>No skits found</p>
          ))}
      </div>

      {/* — REPORT MODAL — */}
      {reportingSkit && (
        <ReportModal
          kind="skit"
          onBlowWhistle={handleReport}
          onCancel={() => setReportingSkit(null)}
        />
      )}
    </div>
  );
};

export default UniversalSearchPage;
